// =============================================================================
// src/lib/brandPalette.js
//
// Per-practice brand color identity. Drives:
//   - Modal hero header background (components/ui.jsx <Modal>)
//   - Sidebar "P" logo tile background + foreground
//   - Sidebar active nav pill background + foreground
//   - Primary CTA button background (Btn variant="primary")
//
// System colors (role chips, status pills, semantic states like danger reds,
// success greens, audit feed indicators) do NOT change with the brand palette.
// They encode meaning, not branding, and stay fixed across all practices.
//
// HOW IT WORKS:
// Palettes are applied as CSS custom properties on document.documentElement.
// AuthProvider calls applyBrandPalette(practice.brand_palette) once on session
// load. Components reference var(--brand-*) instead of hardcoded hex values,
// so swapping the palette automatically rebrands every consumer.
//
// SOURCE OF TRUTH:
// public.practices.brand_palette TEXT (NOT NULL, DEFAULT 'teal' since Oct 2026;
// it was 'black').
// Constrained via CHECK to one of the keys below.
// =============================================================================

export const BRAND_PALETTES = {
  black: {
    name: "Black",
    description: "Premium minimal - Linear/Vercel feel",
    primary:        "#000000",
    primaryDark:    "#1A1A18",
    primaryMid:     "#333333",
    bg:             "#F5F5F5",
    border:         "#E0E0E0",
    contrast:       "#FFFFFF",
    // Black inverts on the dark sidebar - white tile + white pill, since
    // black-on-near-black would disappear into the sidebar background.
    sidebarLogoBg:  "#FFFFFF",
    sidebarLogoFg:  "#000000",
    activeNavBg:    "#FFFFFF",
    activeNavFg:    "#000000",
    sidebarBg:      "#0A0A0A",
    sidebarFg:      "#E5E5E5",
    // Canvas tints are a no-op for black because black mixed into white
    // at any percentage gives gray, which is what the system already had.
    // Keeping them defined keeps the var mapping consistent across palettes.
    canvasBg:       "#F6F6F5",
    surfaceTint:    "#EBEBEA",
  },

  teal: {
    name: "Teal",
    description: "Medical green - the original PracticeOS look",
    primary:        "#0F6E56",
    primaryDark:    "#085041",
    primaryMid:     "#1D9E75",
    bg:             "#E1F5EE",
    border:         "#9FE1CB",
    contrast:       "#FFFFFF",
    sidebarLogoBg:  "#0F6E56",
    sidebarLogoFg:  "#FFFFFF",
    activeNavBg:    "#0F6E56",
    activeNavFg:    "#FFFFFF",
    canvasBg:       "#F4F8F6",
    surfaceTint:    "#ECF5F1",
  },

  navy: {
    name: "Navy",
    description: "Conservative, corporate",
    primary:        "#1E3A8A",
    primaryDark:    "#152554",
    primaryMid:     "#2C5282",
    bg:             "#E6EBF7",
    border:         "#A4B5DD",
    contrast:       "#FFFFFF",
    sidebarLogoBg:  "#1E3A8A",
    sidebarLogoFg:  "#FFFFFF",
    activeNavBg:    "#1E3A8A",
    activeNavFg:    "#FFFFFF",
    canvasBg:       "#F4F6FB",
    surfaceTint:    "#E9EDF6",
  },

  slate: {
    name: "Slate",
    description: "Modern, neutral",
    primary:        "#334155",
    primaryDark:    "#1E293B",
    primaryMid:     "#475569",
    bg:             "#F1F5F9",
    border:         "#CBD5E1",
    contrast:       "#FFFFFF",
    sidebarLogoBg:  "#334155",
    sidebarLogoFg:  "#FFFFFF",
    activeNavBg:    "#334155",
    activeNavFg:    "#FFFFFF",
    canvasBg:       "#F5F6F8",
    surfaceTint:    "#EBEDF1",
  },

  plum: {
    name: "Plum",
    description: "Warm, distinctive",
    primary:        "#6B21A8",
    primaryDark:    "#4C1485",
    primaryMid:     "#7E3CC4",
    bg:             "#F3E8FF",
    border:         "#D8B4FE",
    contrast:       "#FFFFFF",
    sidebarLogoBg:  "#6B21A8",
    sidebarLogoFg:  "#FFFFFF",
    activeNavBg:    "#6B21A8",
    activeNavFg:    "#FFFFFF",
    canvasBg:       "#F8F4FA",
    surfaceTint:    "#F1E9F5",
  },

  burgundy: {
    name: "Burgundy",
    description: "Bold, traditional medical",
    primary:        "#991B1B",
    primaryDark:    "#6B1010",
    primaryMid:     "#B82626",
    bg:             "#FEE2E2",
    border:         "#FCA5A5",
    contrast:       "#FFFFFF",
    sidebarLogoBg:  "#991B1B",
    sidebarLogoFg:  "#FFFFFF",
    activeNavBg:    "#991B1B",
    activeNavFg:    "#FFFFFF",
    canvasBg:       "#FAF4F4",
    surfaceTint:    "#F5E8E8",
  },

  forest: {
    name: "Forest",
    description: "Deep green, traditional medical",
    primary:        "#22543D",
    primaryDark:    "#15331E",
    primaryMid:     "#2F855A",
    bg:             "#E6F4EA",
    border:         "#A1D6B8",
    contrast:       "#FFFFFF",
    sidebarLogoBg:  "#22543D",
    sidebarLogoFg:  "#FFFFFF",
    activeNavBg:    "#22543D",
    activeNavFg:    "#FFFFFF",
    canvasBg:       "#F4F8F5",
    surfaceTint:    "#E8F1EB",
  },

  cerulean: {
    name: "Cerulean",
    description: "Fresh, contemporary",
    primary:        "#0E7490",
    primaryDark:    "#085069",
    primaryMid:     "#0891B2",
    bg:             "#CFFAFE",
    border:         "#67E8F9",
    contrast:       "#FFFFFF",
    sidebarLogoBg:  "#0E7490",
    sidebarLogoFg:  "#FFFFFF",
    activeNavBg:    "#0E7490",
    activeNavFg:    "#FFFFFF",
    canvasBg:       "#F2F8FA",
    surfaceTint:    "#E3F1F5",
  },

  indigo: {
    name: "Indigo",
    description: "Energetic, tech-forward",
    primary:        "#4338CA",
    primaryDark:    "#2D2599",
    primaryMid:     "#5C50DC",
    bg:             "#E0E7FF",
    border:         "#A5B4FC",
    contrast:       "#FFFFFF",
    sidebarLogoBg:  "#4338CA",
    sidebarLogoFg:  "#FFFFFF",
    activeNavBg:    "#4338CA",
    activeNavFg:    "#FFFFFF",
    canvasBg:       "#F4F5FC",
    surfaceTint:    "#E8EBF9",
  },

  // ---------------------------------------------------------------------------
  // Added Jul 2026 - warm, soft, and bold options.
  //
  // Every `primary` here clears 4.5:1 against white, because it carries button
  // labels and the email header band. The "soft" family is deliberately NOT
  // used at pastel values - a sage button at #A8C0A0 with white text is
  // unreadable, so those hues are darkened to a usable depth and the softness
  // shows up in the canvas and surface tints instead.
  //
  // Keys here MUST match public.brand_palettes in the database, which is the
  // source of truth for anything rendered server-side (notably the care-gap
  // email header). Adding one here without the matching DB row means the
  // palette cannot be selected; the CHECK constraint is derived from that table.
  // ---------------------------------------------------------------------------

  rust: {
    name: "Rust",
    description: "Warm, earthy, grounded",
    primary:        "#9A3412",
    primaryDark:    "#7C2D12",
    primaryMid:     "#C2410C",
    bg:             "#FDEBE3",
    border:         "#F5BFA6",
    contrast:       "#FFFFFF",
    sidebarLogoBg:  "#9A3412",
    sidebarLogoFg:  "#FFFFFF",
    activeNavBg:    "#9A3412",
    activeNavFg:    "#FFFFFF",
    canvasBg:       "#FAF6F4",
    surfaceTint:    "#F4EAE5",
  },

  amber: {
    name: "Amber",
    description: "Warm gold - welcoming without being loud",
    primary:        "#92400E",
    primaryDark:    "#78350F",
    primaryMid:     "#B45309",
    bg:             "#FEF3E2",
    border:         "#F5CE96",
    contrast:       "#FFFFFF",
    sidebarLogoBg:  "#92400E",
    sidebarLogoFg:  "#FFFFFF",
    activeNavBg:    "#92400E",
    activeNavFg:    "#FFFFFF",
    canvasBg:       "#FAF8F3",
    surfaceTint:    "#F4EEE3",
  },

  clay: {
    name: "Clay",
    description: "Deep terracotta - warm but serious",
    primary:        "#7C2D12",
    primaryDark:    "#601F0C",
    primaryMid:     "#9A3412",
    bg:             "#FBE8E0",
    border:         "#EDB69C",
    contrast:       "#FFFFFF",
    sidebarLogoBg:  "#7C2D12",
    sidebarLogoFg:  "#FFFFFF",
    activeNavBg:    "#7C2D12",
    activeNavFg:    "#FFFFFF",
    canvasBg:       "#FAF6F4",
    surfaceTint:    "#F3E9E4",
  },

  sage: {
    name: "Sage",
    description: "Muted green - calm, low-clinical",
    primary:        "#4F6F52",
    primaryDark:    "#3A5340",
    primaryMid:     "#688A6B",
    bg:             "#E8F0E8",
    border:         "#B5CDB7",
    contrast:       "#FFFFFF",
    sidebarLogoBg:  "#4F6F52",
    sidebarLogoFg:  "#FFFFFF",
    activeNavBg:    "#4F6F52",
    activeNavFg:    "#FFFFFF",
    canvasBg:       "#F6F8F6",
    surfaceTint:    "#ECF1EC",
  },

  sky: {
    name: "Sky",
    description: "Open blue - light, approachable",
    primary:        "#075985",
    primaryDark:    "#0C4A6E",
    primaryMid:     "#0369A1",
    bg:             "#E0F2FE",
    border:         "#A5D8F3",
    contrast:       "#FFFFFF",
    sidebarLogoBg:  "#075985",
    sidebarLogoFg:  "#FFFFFF",
    activeNavBg:    "#075985",
    activeNavFg:    "#FFFFFF",
    canvasBg:       "#F4F8FA",
    surfaceTint:    "#E8F1F6",
  },

  lavender: {
    name: "Lavender",
    description: "Soft violet - distinctive, non-corporate",
    primary:        "#5B21B6",
    primaryDark:    "#4C1D95",
    primaryMid:     "#7C3AED",
    bg:             "#EDE9FE",
    border:         "#C4B5FD",
    contrast:       "#FFFFFF",
    sidebarLogoBg:  "#5B21B6",
    sidebarLogoFg:  "#FFFFFF",
    activeNavBg:    "#5B21B6",
    activeNavFg:    "#FFFFFF",
    canvasBg:       "#F7F6FB",
    surfaceTint:    "#EFEDF7",
  },

  crimson: {
    name: "Crimson",
    description: "Bold red - high energy",
    primary:        "#BE123C",
    primaryDark:    "#9F1239",
    primaryMid:     "#E11D48",
    bg:             "#FFE4E9",
    border:         "#FBA8BC",
    contrast:       "#FFFFFF",
    sidebarLogoBg:  "#BE123C",
    sidebarLogoFg:  "#FFFFFF",
    activeNavBg:    "#BE123C",
    activeNavFg:    "#FFFFFF",
    canvasBg:       "#FAF5F6",
    surfaceTint:    "#F5E7EB",
  },

  emerald: {
    name: "Emerald",
    description: "Vivid green - fresh, confident",
    primary:        "#047857",
    primaryDark:    "#065F46",
    primaryMid:     "#059669",
    bg:             "#D1FAE5",
    border:         "#8FE0BE",
    contrast:       "#FFFFFF",
    sidebarLogoBg:  "#047857",
    sidebarLogoFg:  "#FFFFFF",
    activeNavBg:    "#047857",
    activeNavFg:    "#FFFFFF",
    canvasBg:       "#F4F9F7",
    surfaceTint:    "#E7F3EE",
  },

  violet: {
    name: "Violet",
    description: "Saturated purple - modern, tech-forward",
    primary:        "#6D28D9",
    primaryDark:    "#5B21B6",
    primaryMid:     "#8B5CF6",
    bg:             "#EDE9FE",
    border:         "#C4B5FD",
    contrast:       "#FFFFFF",
    sidebarLogoBg:  "#6D28D9",
    sidebarLogoFg:  "#FFFFFF",
    activeNavBg:    "#6D28D9",
    activeNavFg:    "#FFFFFF",
    canvasBg:       "#F7F5FC",
    surfaceTint:    "#EFEBF8",
  },

};

// Teal, not Black (Oct 2026). Black is the palette with the least room in dark
// mode (pure black primary), and a default should be the palette where every
// brand-coloured label reads best for a practice that never picked one. Teal
// is also the portal's fallback (PortalShell), so staff and patients now match.
// Practices that chose Black keep it; this only changes the unpicked case.
export const DEFAULT_BRAND_PALETTE = "teal";

// Look up a palette by key. Falls back to the default if the key is unknown
// (defensive against bad DB values from future palette additions).
export function getBrandPalette(name) {
  return BRAND_PALETTES[name] || BRAND_PALETTES[DEFAULT_BRAND_PALETTE];
}

// Apply a palette by setting CSS variables on document.documentElement.
// Components reference these vars (e.g. background: 'var(--brand-primary)')
// so swapping the palette automatically rebrands every consumer.
//
// Call this once on session load (in AuthProvider) with the practice's
// brand_palette value, and any time the practice changes their selection
// in Settings.
export function applyBrandPalette(name) {
  // Remember the last palette so a theme change can re-derive the surface
  // variables without the caller having to know about theming.
  if (typeof window !== "undefined") {
    window.__posLastBrandPalette = name;
    window.__posReapplyBrandPalette = () => applyBrandPalette(window.__posLastBrandPalette);
  }
  if (typeof document === "undefined") return;  // SSR safety

  const p = getBrandPalette(name);
  const root = document.documentElement;

  root.style.setProperty("--brand-primary",          p.primary);
  // RGB triplet for translucent brand tints (glass shadows, celebration glow).
  const hex = String(p.primary || "#0F6E56").replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16), g = parseInt(hex.substring(2, 4), 16), b = parseInt(hex.substring(4, 6), 16);
  root.style.setProperty("--brand-primary-rgb", [r, g, b].map((n) => (Number.isFinite(n) ? n : 0)).join(", "));
  // Theme flags, declared BEFORE anything reads them. These were previously
  // declared further down, so --brand-text referenced isDark inside its own
  // temporal dead zone and threw at module init - a blank page, not a styling
  // bug.
  const isDark = typeof document !== "undefined" && document.documentElement.getAttribute("data-theme") === "dark";
  const darkSurface = (fallback) => (isDark ? fallback : null);

  // --brand-primary is an IDENTITY colour and is often deep enough to be
  // unreadable as text on a dark surface. --brand-text is the same hue lifted
  // for legibility; anything that draws brand-coloured TEXT or an icon should
  // use it. Buttons, active nav labels and link-style actions all do.
  // ---- Derived dark variants -------------------------------------------
  // 18 palettes x ~14 colour keys is 252 values; hand-authoring dark twins for
  // all of them would rot the moment a 19th palette is added. Derive instead,
  // from the one value that defines the palette: primary.
  //
  // The problem being solved: several palettes (black #000000, navy, burgundy,
  // forest) have primaries dark enough to disappear against a dark panel. A
  // "black" practice would get a black button on a near-black surface. So any
  // brand colour that has to READ against the dark canvas is lightened along
  // its own hue until it clears a contrast floor, and the hue is preserved so
  // the practice still recognises its colour.
  const DARK_PANEL = [28, 31, 30];              // --pos-bg-primary in dark
  const relLum = ([rr, gg, bb]) => {
    const f = (v) => { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
    return 0.2126 * f(rr) + 0.7152 * f(gg) + 0.0722 * f(bb);
  };
  const contrast = (x, y) => {
    const a = relLum(x), b = relLum(y);
    return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
  };
  const toRgbStr = (c) => "rgb(" + c.map((v) => Math.max(0, Math.min(255, Math.round(v)))).join(", ") + ")";
  // Lighten toward white in small steps until the ratio clears `min`.
  const ensureContrast = (c, min) => {
    let out = c.slice();
    for (let t = 0; t <= 0.95; t += 0.05) {
      out = c.map((v) => v + (255 - v) * t);
      if (contrast(out, DARK_PANEL) >= min) break;
    }
    return out;
  };
  const rgbOf = (hexStr, fallback) => {
    const h = String(hexStr || "").replace("#", "");
    if (h.length !== 6) return fallback;
    const v = [0, 2, 4].map((i) => parseInt(h.substring(i, i + 2), 16));
    return v.every(Number.isFinite) ? v : fallback;
  };

  // --brand-text: brand-coloured TEXT. Needs 4.5:1 for body legibility.
  root.style.setProperty(
    "--brand-text",
    isDark ? toRgbStr(ensureContrast([r, g, b], 4.5)) : (p.primaryDark || p.primary)
  );
  // --brand-primary-accessible: brand as a FILL that must still be visible as a
  // shape (buttons, bars, dots). 3:1 is the non-text threshold.
  root.style.setProperty(
    "--brand-primary-accessible",
    isDark ? toRgbStr(ensureContrast([r, g, b], 3)) : p.primary
  );
  // Liquid glass only where the browser can render it; everything else keeps the solid fallback (UI Excellence decision 2).
  if (typeof CSS !== "undefined" && CSS.supports && (CSS.supports("backdrop-filter", "blur(20px)") || CSS.supports("-webkit-backdrop-filter", "blur(20px)"))) {
    root.classList.add("pos-glass");
  }
  // primaryDark and primaryMid are deeper still, and are read as text in a
  // number of places. On dark they resolve to the derived text colour.
  root.style.setProperty("--brand-primary-dark",
    isDark ? toRgbStr(ensureContrast(rgbOf(p.primaryDark, [r, g, b]), 4.5)) : p.primaryDark);
  root.style.setProperty("--brand-primary-mid",
    isDark ? toRgbStr(ensureContrast(rgbOf(p.primaryMid, [r, g, b]), 4.5)) : p.primaryMid);
  // Dark mode: the brand SURFACE variables must not stay light. These are the
  // tinted backgrounds behind page headers, active nav and card washes; leaving
  // them light while text flips to a pale colour is exactly what made every page
  // title invisible. The brand IDENTITY colours (primary, contrast, logo) are
  // untouched - the practice's colour still identifies the practice.

  root.style.setProperty("--brand-bg",               darkSurface("rgba(var(--brand-primary-rgb, 15,110,86), 0.14)") || p.bg);
  root.style.setProperty("--brand-border",           darkSurface("rgba(var(--brand-primary-rgb, 15,110,86), 0.30)") || p.border);
  root.style.setProperty("--brand-contrast",         p.contrast);
  root.style.setProperty("--brand-sidebar-logo-bg",  p.sidebarLogoBg);
  root.style.setProperty("--brand-sidebar-logo-fg",  p.sidebarLogoFg);
  root.style.setProperty("--brand-active-nav-bg",    darkSurface("rgba(var(--brand-primary-rgb, 15,110,86), 0.22)") || p.activeNavBg);
  root.style.setProperty("--brand-active-nav-fg",    darkSurface("var(--brand-text)") || p.activeNavFg);
  // NOTE: --brand-sidebar-bg / --brand-sidebar-fg are deliberately NOT set.
  // The sidebar is intentionally palette-independent: its background is the
  // fixed C.navBg token, and only the logo tile and active-nav pill retint
  // per palette. Writing those two variables implied a themed sidebar that
  // does not exist - 17 of 18 palettes never defined the keys, so they were
  // being set to undefined - and the next person to touch Layout.jsx would
  // reasonably have assumed var(--brand-sidebar-bg) was live.
  //
  // If a fully brand-tinted sidebar is ever wanted, that is a design
  // decision rather than a bug fix: define sidebarBg/sidebarFg across all
  // palettes, restore these two lines, then point C.navBg at
  // var(--brand-sidebar-bg, #0a0a0a). Be aware a tinted sidebar is far more
  // visually assertive than near-black with a coloured tile and pill.
  // Canvas tint (~4% primary) and surface tint (~6%) - applied to the
  // outer page canvas and to grayish UI strips (tab bars, table headers)
  // so the whole interface picks up a subtle hue of the brand color.
  root.style.setProperty("--brand-canvas",           darkSurface("#111413") || p.canvasBg);
  root.style.setProperty("--brand-surface-tint",     darkSurface("rgba(var(--brand-primary-rgb, 15,110,86), 0.07)") || p.surfaceTint);

  // Tag the html element so CSS selectors can also target the active palette

  // Tag the html element so CSS selectors can also target the active palette
  // if they ever need palette-specific overrides (e.g. data-brand="black").
  root.setAttribute("data-brand", p === BRAND_PALETTES.black ? "black"
                                : p === BRAND_PALETTES.teal  ? "teal"
                                : name);
}

// Convenience for the Settings picker UI - returns the curated list with
// display metadata, in the order they should appear in the swatch grid.
export function listBrandPalettes() {
  return Object.entries(BRAND_PALETTES).map(([key, p]) => ({
    key,
    name: p.name,
    description: p.description,
    primary: p.primary,
  }));
}
